import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useForm } from 'react-hook-form';
import { Star, MessageCircle, User, CalendarDays, Send } from 'lucide-react';
import { getReviews, createReview } from '../services/api';

const Reviews = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 5;
  const maxCharacters = 250; // Maximum character count for reviews
  const [reviewText, setReviewText] = useState('');
  const [emailText, setEmailText] = useState('');
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  // Function to count characters
  const countCharacters = (text) => {
    return text.length;
  };

  // Function to check if email already exists
  const isEmailDuplicate = (email) => {
    return reviews.some(review => 
      review.email && review.email.toLowerCase() === email.toLowerCase()
    );
  };

  // Fetch reviews from Strapi
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const response = await getReviews();
        console.log('Reviews API Response:', response);
        
        if (response.data && response.data.length > 0) {
          const transformedReviews = response.data.map(review => ({
            id: review.id,
            name: `${review.firstName} ${review.lastName}`,
            rating: review.rating,
            date: review.date || review.createdAt,
            comment: review.review,
            email: review.email, // Include email for duplicate checking
            location: 'Mughalpura, Lahore', // Default location
            verified: true
          }));
          setReviews(transformedReviews);
        }
      } catch (err) {
        setError('Failed to load reviews');
        console.error('Error fetching reviews:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const averageRating = reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length;
  const totalReviews = reviews.length;
  
  // Pagination logic
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);
  const startIndex = (currentPage - 1) * reviewsPerPage;
  const endIndex = startIndex + reviewsPerPage;
  const currentReviews = reviews.slice(startIndex, endIndex);
  
  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const onSubmit = async (data) => {
    // Validate rating
    if (selectedRating === 0) {
      setError('Please select a rating before submitting.');
      return;
    }

    // Validate character count
    const characterCount = countCharacters(data.review);
    if (characterCount > maxCharacters) {
      setError(`Review is too long. Please keep it under ${maxCharacters} characters. Current: ${characterCount} characters.`);
      return;
    }

    // Check for duplicate email
    const existingReview = reviews.find(review => 
      review.email && review.email.toLowerCase() === data.email.toLowerCase()
    );
    
    if (existingReview) {
      setError('You have already submitted a review with this email address. Each email can only submit one review.');
      return;
    }

    setIsSubmitting(true);
    setError(null); // Clear any previous errors
    
    try {
      const reviewData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        rating: selectedRating,
        review: data.review
      };

      console.log('Submitting review:', reviewData);
      const result = await createReview(reviewData);
      console.log('Review submitted successfully:', result);
      
      setSubmitSuccess(true);
      reset();
      setSelectedRating(0);
      setReviewText(''); // Reset review text
      setEmailText(''); // Reset email text
      setCurrentPage(1); // Reset to first page when new review is added
      
      // Refresh reviews
      const response = await getReviews();
      console.log('Refreshed reviews:', response);
      
      if (response.data && response.data.length > 0) {
        const transformedReviews = response.data.map(review => ({
          id: review.id,
          name: `${review.firstName} ${review.lastName}`,
          rating: review.rating,
          date: review.date || review.createdAt,
          comment: review.review,
          email: review.email, // Include email for duplicate checking
          location: 'Mughalpura, Lahore',
          verified: true
        }));
        setReviews(transformedReviews);
      }
      
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Customer Reviews - Mughal Ainak Mahal | Mughalpura, Lahore</title>
        <meta name="description" content="Read customer reviews and testimonials for Mughal Ainak Mahal. See what our satisfied customers say about our eyewear services in Mughalpura, Lahore." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-dark text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Customer Reviews
            </h1>
            <p className="text-xl max-w-2xl mx-auto opacity-90">
              See what our satisfied customers say about our services
            </p>
          </div>
        </div>
      </section>

      {/* Overall Rating */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="flex items-center justify-center mb-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-8 h-8 ${
                    i < Math.floor(averageRating)
                      ? 'text-yellow-400 fill-current'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <h2 className="text-3xl font-bold text-secondary mb-2">
              {averageRating.toFixed(1)} out of 5
            </h2>
            <p className="text-gray-600 mb-4">
              Based on {totalReviews} customer reviews
            </p>
            <div className="flex items-center justify-center space-x-4">
              <div className="flex items-center">
                <MessageCircle className="w-5 h-5 text-primary mr-2" />
                <span className="text-secondary font-medium">{totalReviews} Reviews</span>
              </div>
              <div className="flex items-center">
                <User className="w-5 h-5 text-primary mr-2" />
                <span className="text-secondary font-medium">Verified Customers</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-lg text-gray-600">Loading reviews...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500 text-lg">{error}</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No reviews yet. Be the first to share your experience!</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-8">
                {currentReviews.map((review) => (
                <div
                  key={review.id}
                  className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition duration-300 relative min-h-[200px]"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary bg-opacity-10 rounded-full flex items-center justify-center text-primary font-semibold">
                        {review.name.charAt(0)}
                      </div>
                      <div className="ml-3">
                        <h3 className="font-semibold text-secondary">{review.name}</h3>
                      </div>
                    </div>
                    {review.verified && (
                      <span className="text-green-500 text-sm font-medium">✓ Verified</span>
                    )}
                  </div>

                  <div className="flex items-center mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < review.rating
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="text-sm text-gray-500 ml-2">({review.rating})</span>
                  </div>

                  <p className="text-gray-600 mb-4 italic pr-4">"{review.comment}"</p>

                  <div className="absolute bottom-4 left-6 flex items-center text-sm text-gray-500">
                    <CalendarDays className="w-4 h-4 mr-1" />
                    {formatDate(review.date)}
                  </div>
                </div>
                ))}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center mt-8 space-x-2">
                  <button
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                  >
                    Previous
                  </button>
                  
                  <div className="flex space-x-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                      <button
                        key={pageNumber}
                        onClick={() => goToPage(pageNumber)}
                        className={`px-3 py-2 rounded-lg transition duration-200 ${
                          currentPage === pageNumber
                            ? 'bg-primary text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                  >
                    Next
                  </button>
                </div>
              )}
              
              {/* Page Info */}
              <div className="text-center mt-4 text-gray-600">
                Showing {startIndex + 1} of {totalReviews} reviews
              </div>
            </>
          )}
        </div>
      </section>

      {/* Add Review Form */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary mb-4">
              Share Your Experience
            </h2>
            <p className="text-lg text-gray-600">
              Help others by sharing your experience with Mughal Ainak Mahal
            </p>
          </div>

          {submitSuccess && (
            <div className="mb-8 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg text-center">
              Thank you for your review! It has been successfully submitted.
            </div>
          )}

          {error && (
            <div className="mb-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center">
              {error}
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    {...register('firstName', { required: 'First name is required' })}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.firstName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your first name"
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    {...register('lastName', { required: 'Last name is required' })}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.lastName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your last name"
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    },
                    validate: (value) => {
                      if (isEmailDuplicate(value)) {
                        return 'This email has already been used to submit a review.';
                      }
                      return true;
                    }
                  })}
                  value={emailText}
                  onChange={(e) => setEmailText(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.email || isEmailDuplicate(emailText) ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your email"
                />
                <div className="mt-1">
                  {errors.email && (
                    <p className="text-red-500 text-sm">{errors.email.message}</p>
                  )}
                  {emailText && isEmailDuplicate(emailText) && !errors.email && (
                    <p className="text-red-500 text-sm">This email has already been used to submit a review.</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating *
                </label>
                <div className="flex items-center space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setSelectedRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="focus:outline-none"
                    >
                      <Star
                        className={`w-8 h-8 ${
                          star <= (hoverRating || selectedRating)
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                  <span className="text-sm text-gray-500 ml-2">
                    {selectedRating > 0 ? `${selectedRating} star${selectedRating > 1 ? 's' : ''}` : 'Select rating'}
                  </span>
                </div>
                {selectedRating === 0 && (
                  <p className="text-red-500 text-sm mt-1">Please select a rating</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Review *
                </label>
                <textarea
                  {...register('review', { 
                    required: 'Review is required',
                    validate: (value) => {
                      const characterCount = countCharacters(value);
                      if (characterCount > maxCharacters) {
                        return `Review must be ${maxCharacters} characters or less. Current: ${characterCount} characters.`;
                      }
                      return true;
                    }
                  })}
                  rows={4}
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.review ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Share your experience with our services..."
                />
                <div className="flex justify-between items-center mt-2">
                  <div>
                    {errors.review && (
                      <p className="text-red-500 text-sm">{errors.review.message}</p>
                    )}
                  </div>
                  <div className={`text-sm ${countCharacters(reviewText) > maxCharacters ? 'text-red-500' : 'text-gray-500'}`}>
                    {countCharacters(reviewText)} / {maxCharacters} characters
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || selectedRating === 0 || countCharacters(reviewText) > maxCharacters || isEmailDuplicate(emailText)}
                className="w-full bg-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-dark transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Review
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary mb-4">
              Why Customers Trust Us
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're committed to providing the best eyewear experience
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-secondary">Verified Reviews</h3>
              <p className="text-gray-600">All reviews are from verified customers who have used our services</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-secondary">Real Customers</h3>
              <p className="text-gray-600">Authentic feedback from real customers in Mughalpura and Lahore</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-secondary">Transparent Feedback</h3>
              <p className="text-gray-600">We publish all reviews to maintain transparency and trust</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews; 
